// /src/pages/EditDesign.jsx
import React, { useEffect, useMemo, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { motion } from "framer-motion";
import Select from "react-select";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";

import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import {
  useGetDesignByIdQuery,
  useUpdateDesignMutation,
} from "../toolkitServices/designApi";

// select styles factory (red border when hasError)
const makeSelectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    height: 40,
    borderWidth: 1,
    borderColor: hasError ? "#ef4444" : state.isFocused ? "#9481ce" : "#e5e7eb",
    boxShadow: "none",
    ":hover": { borderColor: hasError ? "#ef4444" : "#9481ce" },
    borderRadius: 10,
    fontSize: "0.92rem",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  indicatorsContainer: (base) => ({ ...base, height: 40 }),
  menu: (base) => ({ ...base, borderRadius: 10, overflow: "hidden" }),
  option: (base, state) => ({
    ...base,
    padding: "8px 12px",
    backgroundColor: state.isSelected ? "#efeafd" : state.isFocused ? "#f7f6ff" : "white",
    color: "#111827",
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af" }),
  singleValue: (base) => ({ ...base, color: "#111827" }),
});

// shared input classes (toggle red styles if invalid)
const baseInput = "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing = "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing = "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

// helper: normalize id (handles string/object)
const getId = (x) => (x && typeof x === "object" ? x._id || x.id || x.value || "" : x || "");

// stable getters for react-select equality
const getOptValue = (o) => String(o?.value ?? "");
const getOptLabel = (o) => String(o?.label ?? "");

const EditDesign = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // master data
  const { data: catResp, isLoading: catsLoading } = useGetAllCategoriesQuery({
    page: 1, limit: 200, search: "",
  });
  const { data: prodResp, isLoading: prodsLoading } = useGetAllProductsQuery({
    page: 1, limit: 500, search: "",
  });
  const categories = catResp?.data || [];
  const products = prodResp?.data || [];

  // design
  const { data: designResp, isLoading: isDesignLoading, error: designError } = useGetDesignByIdQuery(id);

  const [updateDesign, { isLoading: isSaving, isSuccess, error: saveError, data: saveResp }] =
    useUpdateDesignMutation();

  // form
  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    clearErrors,
    watch,
    getValues,
    formState: { errors, isSubmitting },
    setFocus,
  } = useForm({
    defaultValues: {
      design: "",
      label: "",
      slug: "",
      description: "",
      category: null,
      product: null,
    },
    mode: "onBlur",
  });

  const mountedRef = useRef(false);

  // populate defaults
  useEffect(() => {
    if (!designResp?.data) return;
    const d = designResp.data;

    const catVal = d.categoryId ? getId(d.categoryId) : "";
    const prodVal = d.productId ? getId(d.productId) : "";

    reset({
      design: d.design || "",
      label: d.label || "",
      slug: d.slug || "",
      description: d.description || "",
      category: d.categoryId ? { label: d.categoryName || d?.categoryId?.name || "", value: catVal } : null,
      product: d.productId ? { label: d.productName || d?.productId?.productName || "", value: prodVal } : null,
    });

    requestAnimationFrame(() => setFocus("design"));
  }, [designResp, reset, setFocus]);

  // toasts
  useEffect(() => {
    if (isSuccess && saveResp?.success) {
      toast.success("Design updated!");
      navigate("/masters/design-master-list"); // redirect on save
    }
  }, [isSuccess, saveResp, navigate]);

  useEffect(() => {
    if (saveError) {
      const msg = saveError?.data?.msg || saveError?.data?.message || saveError?.error || "Update failed!";
      toast.error(msg);
    }
  }, [saveError]);

  // hotkeys
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "Backspace") {
        e.preventDefault();
        navigate(-1);
      }
      if ((e.ctrlKey && e.key.toLowerCase() === "s") || (e.key === "Enter" && e.ctrlKey)) {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate, handleSubmit]);

  // options
  const categoryOptions = useMemo(
    () => categories.map((c) => ({ label: c.name, value: getId(c._id ?? c) })),
    [categories]
  );

  const selectedCategory = watch("category");
  const productOptions = useMemo(() => {
    if (!products?.length) return [];
    const catId = getId(selectedCategory?.value);
    return products
      .filter((p) => {
        const pid = getId(p.categoryId);
        return !catId || String(pid) === String(catId);
      })
      .map((p) => ({
        label: p.productName || p.name,
        value: getId(p._id ?? p.id ?? p),
        categoryId: getId(p.categoryId),
      }));
  }, [products, selectedCategory]);

  // reconcile both category & product once options are ready (prefill visible)
  useEffect(() => {
    // category
    const curCat = getValues("category");
    if (curCat?.value && categoryOptions.length) {
      const catMatch = categoryOptions.find((o) => getOptValue(o) === String(getId(curCat.value)));
      if (catMatch && (curCat.label !== catMatch.label || curCat.value !== catMatch.value)) {
        setValue("category", catMatch, { shouldValidate: false });
      }
    }
    // product
    const curProd = getValues("product");
    if (curProd?.value && productOptions.length) {
      const prodMatch = productOptions.find((o) => getOptValue(o) === String(getId(curProd.value)));
      if (prodMatch && (curProd.label !== prodMatch.label || curProd.value !== prodMatch.value)) {
        setValue("product", prodMatch, { shouldValidate: false });
      }
    }
  }, [categoryOptions, productOptions, getValues, setValue]);

  // when category changes, clear product WITHOUT immediate validation
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    setValue("product", null, { shouldValidate: false, shouldDirty: true });
    clearErrors("product");
  }, [selectedCategory?.value, setValue, clearErrors]);

  const onSubmit = async (form) => {
    const payload = {
      design: (form.design || "").trim(),
      label: (form.label || "").trim().toUpperCase(),
      slug: (form.slug || "").trim().toLowerCase(),
      description: (form.description || "").trim(),
      categoryId: getId(form.category?.value),
      categoryName: form.category?.label,
      productId: getId(form.product?.value),
      productName: form.product?.label,
    };
    try {
      await updateDesign({ id, ...payload }).unwrap();
    } catch {
      /* toast handled in effect */
    }
  };

  if (designError) {
    return <div className="p-6 text-center text-red-500">Failed to load design.</div>;
  }

  const loading = isSaving || isSubmitting || isDesignLoading || catsLoading || prodsLoading;

  return (
    <div className="px-4 py-4">
      {/* Header */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to={"/masters/design-master-list"}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Design List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>
          <div className="flex-1">
            <h2 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">Edit Design</h2>
            <p className="text-xs text-gray-500">
              Tips: <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">S</kbd> or{" "}
              <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Enter</kbd> to save,
                <kbd className="px-1 bg-gray-100 rounded border ml-2">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Back Spc</kbd> go to list
            </p>
          </div>
        </div>
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
      >
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
          {/* Design */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Design<span className="text-red-500">*</span>
            </label>
            <input
              {...register("design", { required: true, minLength: 2, maxLength: 120 })}
              aria-invalid={!!errors.design}
              placeholder="Enter design name"
              className={`${baseInput} ${!!errors.design ? errRing : okRing}`}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Category<span className="text-red-500">*</span>
            </label>
            <Controller
              name="category"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={categoryOptions}
                  placeholder="Select category"
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(!!errors.category)}
                  isDisabled={catsLoading}
                  getOptionValue={getOptValue}
                  getOptionLabel={getOptLabel}
                  onChange={(val) => {
                    field.onChange(val);
                    setValue("product", null, { shouldValidate: false, shouldDirty: true });
                    clearErrors("product");
                  }}
                />
              )}
            />
          </div>

          {/* Product (required only after category chosen) */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Product<span className="text-red-500">*</span>
            </label>
            <Controller
              name="product"
              control={control}
              rules={{
                validate: (v) => {
                  if (!selectedCategory) return true; // not required until category selected
                  return !!v || "required";
                },
              }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={productOptions}
                  placeholder={selectedCategory ? "Select product" : "Select a category first"}
                  isDisabled={!selectedCategory || prodsLoading}
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(!!errors.product && !!selectedCategory)}
                  getOptionValue={getOptValue}
                  getOptionLabel={getOptLabel}
                />
              )}
            />
          </div>

          {/* Label */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Label
              <span className="text-xs font-normal text-gray-400 ml-1">(4 chars, A–Z / 0–9)</span>
            </label>
            <input
              {...register("label", {
                validate: (v) => !v || /^[A-Z0-9]{4}$/.test(v),
                onChange: (e) => {
                  e.target.value = e.target.value.toUpperCase().slice(0, 4);
                },
              })}
              disabled={id}
              aria-invalid={!!errors.label}
              placeholder="e.g. D001"
              className={`${baseInput} ${!!errors.label ? errRing : okRing} tracking-widest`}
              maxLength={4}
              autoComplete="off"
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Slug</label>
            <input
              {...register("slug")}
              aria-invalid={false}
              placeholder="design-slug"
              className={`${baseInput} ${okRing}`}
              autoComplete="off"
            />
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Description</label>
            <textarea
              {...register("description")}
              aria-invalid={false}
              rows={2}
              placeholder="Enter description"
              className={`${baseInput} ${okRing} resize-y`}
            />
          </div>

          {/* Submit */}
          <div className="md:col-span-2 flex flex-col sm:flex-row gap-2 pt-1">
            <motion.button
              whileHover={{ scale: isSaving ? 1 : 1.01 }}
              whileTap={{ scale: isSaving ? 1 : 0.98 }}
              type="submit"
              disabled={isSaving || isDesignLoading}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </motion.button>

            <Link
              to="/masters/design-master-list"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              title="Cancel and go back"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EditDesign;
